import { Box, Button, Divider, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import ExportButton from "components/button/ExportButton";
import { useNavigate } from "react-router";
import routes from "constants/routes";
import { SESSION_KEY } from "constants/key";
import LabelValueField from "components/Label/LabelValueField";
import LabelValueDisplay from "components/LabelValueDisplay";
import Notify from "config/notify";
import WheelItemDetail from "features/mst_store_tire_wheel_filter/components/WheelItemDetail";
import FilterOptionModal from "features/mst_store_tire_wheel_filter/components/FilterOptionModal";
import { FastenerState, IInfoCell } from "features/mst_store_tire_wheel_filter/types/wheel";
import { OptionItem } from "features/mst_option/types/option";
import {
  useQuoteWheel,
  calculateWheelPrice,
} from "features/mst_store_tire_wheel_filter/hooks/useQuoteWheel";
import { formatCurrency } from "utils";

// Constants
const DISABLED_TEXT = "なし" as const;
const CSS_VARIABLES = {
  GREEN: "var(--green-600)",
  OFFWHITE: "var(--offwhite-500)",
  GRAY: "var(--gray-700)",
  BLUE: "var(--blue-400)",
  BLACK: "var(--black)",
  PRIMARY: "var(--primary-500)",
} as const;

type FlagFieldMapping = {
  [key: string]: {
    valueField: keyof FastenerState;
    getDefaultValue: (options: any) => string;
  };
};

// Helper functions
const getBackgroundColor = (isActive: boolean) =>
  isActive ? CSS_VARIABLES.GREEN : CSS_VARIABLES.OFFWHITE;

const mapToSelectOption = (data: OptionItem[]) =>
  data.map((el) => ({
    label: `${el.option_name}\u00A0\u00A0${formatCurrency(el.sale_price || 0)}円`,
    value: el.option_cd || `${el.id}`,
  }));

const styles = {
  title: {
    fontWeight: "medium",
    color: CSS_VARIABLES.GRAY,
    fontSize: { xs: "25px", xl: "33px" },
  },
  button: {
    fontWeight: "medium",
    fontSize: { xs: "16px", md: "20px", xl: "20px" },
  },
  titleSelection: {
    fontSize: { xs: "20px", xl: "26px" },
    bgcolor: CSS_VARIABLES.BLUE,
    height: "48px",
    px: 6,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "fit-content",
  },
  divider: {
    width: "100%",
    marginBottom: 1.5,
    marginTop: 0,
    borderBottom: `3px solid ${CSS_VARIABLES.BLUE}`,
  },
} as const;

/**
 * StoreFilterContainer component displays the main interface for selecting wheels and related options.
 *
 * Features:
 * - Displays selected car, tire type, and size.
 * - Allows navigation back to tire selection.
 * - Provides options to change additional items (fasteners).
 * - Shows a modal for filtering options.
 * - Displays a list of wheel items and allows sorting/filtering.
 *
 * State:
 * - fasteners: Tracks the checked state of nut, bolt, and washer options.
 * - showFilterModal: Controls the visibility of the filter modal.
 * @param props - Component props
 * @param props.type - Type of selection ("wheel" or "tire")
 */
export default function StoreFilterContainer({ type }: { type: "wheel" | "tire" }) {
  const {
    carImage,
    carInfo,
    paramReq,
    options,
    wheelDetail,
    visibleWheelDetail,
    visibleWheelCount,
    toggleSortOrder,
    resetRecommendOrder,
    makerNames,
    onHandleFilter,
    fasteners,
    setFasteners,
    optionsPrice,
    discountRate,
    setDiscountRate,
    onHandleSelectedWheels,
    selectedWheels,
    sortOrder,
    sortedIds,
    costPriceMap,
    setDiscountFocusWheelId,
  } = useQuoteWheel();

  const [showFilterModal, setShowFilterModal] = useState(false);

  const navigate = useNavigate();

  // Flag field mapping for cleaner logic
  const flagFieldMapping: FlagFieldMapping = useMemo(
    () => ({
      nut_flag: {
        valueField: "nut",
        getDefaultValue: (opts) => String(opts.nat[0]?.option_cd || opts.nat[0]?.id || ""),
      },
      lock_nut_flag: {
        valueField: "lock_nut",
        getDefaultValue: (opts) => String(opts.lockNat[0]?.option_cd || opts.lockNat[0]?.id || ""),
      },
      hub_ring_flag: {
        valueField: "hub_ring",
        getDefaultValue: (opts) => String(opts.hub[0]?.option_cd || opts.hub[0]?.id || ""),
      },
      wheel_coating_flag: {
        valueField: "wheel_coating",
        getDefaultValue: (opts) =>
          String(opts.wcoating[0]?.option_cd || opts.wcoating[0]?.id || ""),
      },
    }),
    [options],
  );

  const handleNavigate = useCallback(() => {
    const route =
      type === "wheel"
        ? routes.mstCarModelTireWheelSelection
        : sessionStorage.getItem(SESSION_KEY.TIRE_WHEEL_TIRE_FROM) || routes.mstProcFee;
    navigate(route);
  }, [navigate, type]);

  const handleFilter = useCallback(() => {
    setShowFilterModal(true);
  }, []);

  const onChangeValue = useCallback(
    (key: string, value: string | boolean) => {
      setFasteners((prev) => {
        const next = { ...prev, [key]: value } as FastenerState;

        // ロックナット変更時はナットをオフ＆クリアし、API は nat のみ再取得（useQuoteWheel）
        if (key === "lock_nut" || key === "lock_nut_flag") {
          next.nut = DISABLED_TEXT;
          next.nut_flag = false;
        }

        // Handle flag-based default values using mapping
        const flagMapping = flagFieldMapping[key];
        if (flagMapping) {
          const targetField = flagMapping.valueField;
          const newFieldValue =
            value === true ? flagMapping.getDefaultValue(options) : DISABLED_TEXT;
          Object.assign(next, { [targetField]: newFieldValue });
        }

        return next;
      });
    },
    [flagFieldMapping, options],
  );

  // Initialize default values when options are loaded and flags are true
  const initializeDefaultValues = useCallback(() => {
    setFasteners((prev) => {
      let changed = false;
      const next = { ...prev } as FastenerState;

      Object.entries(flagFieldMapping).forEach(([flagKey, mapping]) => {
        const flagValue = Boolean(prev[flagKey as keyof FastenerState]);
        const currentValue = prev[mapping.valueField];

        if (flagValue && (!currentValue || currentValue === DISABLED_TEXT)) {
          const newValue = mapping.getDefaultValue(options);
          if (newValue && newValue !== currentValue) {
            Object.assign(next, { [mapping.valueField]: newValue });
            changed = true;
          }
        }
      });

      return changed ? next : prev;
    });
  }, [flagFieldMapping, options]);

  useEffect(() => {
    initializeDefaultValues();
  }, [initializeDefaultValues]);

  const setShowFilterModalCallback = useCallback((show: boolean) => {
    setShowFilterModal(show);
  }, []);

  const handleSetDiscountRate = useCallback(
    (id: number, value: string) => {
      setDiscountRate(id, value);
    },
    [setDiscountRate],
  );

  // Helper function to create fastener item configuration
  const createFastenerItem = useCallback(
    (
      label: string,
      fieldName: keyof FastenerState,
      fieldNameLabel: keyof FastenerState,
      optionsList: any[],
    ): IInfoCell => {
      const flag = Boolean(fasteners[fieldNameLabel]);
      return {
        sxBox: {
          width: { xs: "100%", lg: "calc((100% - 2px) / 2)" },
          minWidth: 250,
        },
        label,
        fieldNameLabel: fieldNameLabel as string,
        labelType: "checkbox",
        sxLabel: {
          backgroundColor: getBackgroundColor(flag),
          whiteSpace: "nowrap",
        },
        value: flag ? (fasteners[fieldName] as string) : DISABLED_TEXT,
        valueType: flag ? "select" : "text",
        fieldName: fieldName as string,
        options: optionsList,
        valueOnly: true,
      };
    },
    [fasteners],
  );

  const onNavigateByType = useCallback(() => {
    if (!selectedWheels.length) {
      Notify.setNotification({
        show: true,
        title: "商品を選択してください",
      });
      return;
    }
    const route = type === "tire" ? routes.mstConfirmProcFee : routes.mstWheelTireProcFee;

    // Recalculate final_price for selected wheels based on current discountRate
    const recalculatedWheelDetails = wheelDetail
      .filter((el) => selectedWheels.includes(el.id))
      .map((wheel) => {
        const discountItem = discountRate.find((dr) => dr.id === wheel.id);
        const discountValue = Number(discountItem?.value ?? 0);
        return calculateWheelPrice(wheel, discountValue, optionsPrice, costPriceMap);
      });

    const costSettingWheel = {
      discount_rate: discountRate.filter((el) => selectedWheels.includes(el.id)),
      cost_setting: fasteners,
      wheels_detail: recalculatedWheelDetails,
      cost_price_map: costPriceMap,
    };
    if (discountRate.filter((el) => selectedWheels.includes(el.id)).some((el) => el.isError)) {
      return;
    }

    // Save to SELECTED_WHEEL for next screen
    sessionStorage.setItem(SESSION_KEY.SELECTED_WHEEL, JSON.stringify(costSettingWheel));

    // Also save to WHEEL_STORE_STATE for back navigation
    const wheelStoreState = {
      selectedWheels,
      discountRate,
      fasteners,
      paramReq,
      sortOrder,
      sortedIds,
    };
    sessionStorage.setItem(SESSION_KEY.WHEEL_STORE_STATE, JSON.stringify(wheelStoreState));

    navigate(route);
  }, [
    type,
    navigate,
    selectedWheels,
    discountRate,
    fasteners,
    paramReq,
    wheelDetail,
    sortOrder,
    sortedIds,
  ]);

  const costItems: IInfoCell[] = useMemo(
    () => [
      createFastenerItem("ナット", "nut", "nut_flag", mapToSelectOption(options.nat)),
      createFastenerItem("ハブリング", "hub_ring", "hub_ring_flag", mapToSelectOption(options.hub)),
      createFastenerItem(
        "ロックナット",
        "lock_nut",
        "lock_nut_flag",
        mapToSelectOption(options.lockNat),
      ),
      createFastenerItem(
        "ホイールコーティング",
        "wheel_coating",
        "wheel_coating_flag",
        mapToSelectOption(options.wcoating),
      ),
    ],
    [fasteners, options, createFastenerItem, mapToSelectOption],
  );

  return (
    <>
      <Box sx={{ px: { xs: 2, md: 3, xl: 5 }, pt: { xs: 2, md: 2.5, lg: 3 } }}>
        <Button onClick={() => navigate(-1)} variant="custom-outlined">
          <Box>＜ 戻る</Box>
        </Button>
      </Box>
      <Box
        display="flex"
        flexWrap="wrap"
        justifyContent="space-between"
        gap={3}
        sx={{ p: { xs: 2, md: 3, xl: 5 } }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          flexDirection="column"
          alignItems="center"
          sx={{ minWidth: 255, width: { sm: "100%", lg: "14%" }, gap: 2.5 }}
        >
          <Typography variant="h2" sx={{ ...styles.title, textAlign: "left" }}>
            ホイールを選ぶ
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
            <Box sx={{ maxWidth: 300, height: "100%", maxHeight: { xs: 200, md: 130 } }}>
              <img src={carImage} style={{ height: "100%", width: "100%" }} alt="car_image" />
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            gap: 2.5,
            width: { xs: "100%", md: "50%", lg: "35%", xl: "45%" },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: { xs: 2, xl: 4 },
              alignItems: "center",
              width: "100%",
            }}
          >
            <ExportButton
              sx={{ ...styles.button, width: { xl: 260, xs: "fit-content" } }}
              label={type === "tire" ? "タイヤ選択へ戻る" : "車種選択へ戻る"}
              onClick={handleNavigate}
            />
            <Typography
              sx={{
                fontWeight: "medium",
                fontSize: { xs: "16px", xl: "21px" },
                color: "var(--black)",
              }}
            >
              {type === "tire"
                ? "※ホイールは最大4種類まで選択できます"
                : "※ホイールは1種類のみ選択できます"}
            </Typography>
          </Box>

          <Box
            display="flex"
            flexWrap="wrap"
            gap="2px"
            sx={{
              justifyContent: { xs: "center", xl: "flex-start" },
              width: { xs: "100%", lg: "90%" },
            }}
          >
            {carInfo.map((el) => (
              <LabelValueDisplay
                key={el.label}
                label={el.label}
                value={el.value}
                containerSx={{ width: { xs: "100%", md: "calc((100% - 2px) / 2)" } }}
              />
            ))}
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
            width: { xs: "100%", md: "45%", lg: "40%", xl: "38%" },
          }}
        >
          <Typography sx={{ ...styles.title, textAlign: "right" }}>オプション品</Typography>
          <Box display="flex" gap="2px" flexWrap="wrap" justifyContent="flex-end">
            {costItems.map((el) => (
              <LabelValueField
                key={el.fieldName}
                sxBox={el.sxBox}
                label={el.label}
                sxLabel={el.sxLabel}
                labelType={el.labelType}
                value={el.value}
                valueType={el.valueType}
                sxValue={el.sxValue}
                options={el.options}
                valueOnly={el.valueOnly}
                checked={Boolean(fasteners[el.fieldNameLabel as never])}
                onChangeLabel={(e) => onChangeValue(el.fieldNameLabel as never, e.target.checked)}
                onChangeValue={(e) => onChangeValue(el.fieldName, e.target.value)}
              />
            ))}
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "end",
          gap: 2,
          flexWrap: "wrap",
          px: { xs: 2, md: 3, xl: 5 },
          pr: 3,
        }}
      >
        <Typography sx={{ fontSize: { xs: 16, md: 18, lg: 21 } }}>
          件数：{visibleWheelCount}件
        </Typography>
      </Box>
      <Divider sx={styles.divider} />
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "start", md: "center" },
          justifyContent: "space-between",
          px: { xs: 2, md: 3, xl: 5 },
          pr: 2,
          gap: { xs: 3, md: 4 },
          mb: 1.5,
        }}
      >
        {type !== "tire" && (
          <Box
            sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: { xs: 1, lg: 2 } }}
          >
            <ExportButton
              sx={{ ...styles.button, minWidth: 274, width: { xl: 328, xs: "fit-content" } }}
              label="選択した商品でタイヤを選ぶ"
              onClick={onNavigateByType}
            />
            <Typography sx={{ fontSize: { xs: 16, md: 18, lg: 21 } }}>
              ※ホイールは1種類のみ選択できます
            </Typography>
          </Box>
        )}

        {type === "tire" && (
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2 }}>
            <ExportButton
              sx={{ ...styles.button, minWidth: 274, width: { xl: 328, xs: "fit-content" } }}
              label="選択した商品を見積表へ"
              onClick={onNavigateByType}
            />
            <Typography sx={{ fontSize: { xs: 16, md: 18, lg: 21 } }}>
              ※ホイールは最大4種類まで選択できます
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            flexWrap: "wrap",
            width: "auto",
            justifyContent: { xs: "end", lg: "space-between" },
          }}
        >
          <ExportButton
            sx={{ ...styles.button, minWidth: 123, width: { xl: 162, xs: "fit-content" } }}
            label="絞り込み"
            onClick={handleFilter}
          />
          <ExportButton
            sx={{ ...styles.button, minWidth: 123, width: { xl: 162, xs: "fit-content" } }}
            label="おすすめ順"
            onClick={resetRecommendOrder}
          />
          <ExportButton
            sx={{ ...styles.button, minWidth: 172, width: { xl: 205, xs: "fit-content" } }}
            onClick={toggleSortOrder}
            label="価格で並び替え"
          />
        </Box>
      </Box>
      {wheelDetail.length > 0 && (
        <WheelItemDetail
          wheelsData={visibleWheelDetail}
          type={type}
          optionsPrice={optionsPrice}
          discountRate={discountRate}
          onSelectionChange={onHandleSelectedWheels}
          setDiscountRate={handleSetDiscountRate}
          selectedWheels={selectedWheels}
          setDiscountFocusWheelId={setDiscountFocusWheelId}
        />
      )}

      {showFilterModal && (
        <FilterOptionModal
          makerOptions={makerNames}
          showFilterModal={showFilterModal}
          setShowFilterModal={setShowFilterModalCallback}
          onHandleFilter={onHandleFilter}
          defaultValues={{
            maker_names:
              paramReq.maker_names && paramReq.maker_names.length > 0
                ? paramReq.maker_names
                : makerNames.map((m) => m.value),
            winter_flag: paramReq.winter_flag ?? [],
            wheel_color_names: paramReq.wheel_color_names ?? [],
          }}
        />
      )}
    </>
  );
}
